<template>
  <a-modal
    v-model="showModal"
    title="View Ruleset"
    :footer="null"
    width="600px"
    :destroyOnClose="true"
    class="view-ruleset-modal"
  >
    <RulesetSkeleton v-if="loading" />
    <div v-else-if="!hasItems" class="mb-3">
      <Empty>
        <span slot="description" class="text-default">None available rulesets</span>
      </Empty>
    </div>
    <a-tabs v-else type="card" class="mb-2">
      <a-tab-pane
        v-for="(translation, index) in translations"
        :key="index"
        force-render
      >
        <template #tab>
          <DefaultLabelTab
            :name="translation.tab_name"
            :is_default="translation.is_default"
          />
        </template>
        <div class="tab-content-container">
          <div
            v-for="(item, index) in translation.items"
            :key="item.id"
            class="d-flex"
          >
            <div class="d-flex flex-column">
              <span class="d-block mb-1">{{ `Rule ${index + 1}` }}</span>
              <div class="d-flex" style="gap: 20px;">
                <img
                  v-if="item.media"
                  :src="item.media.src"
                  alt="Rule image"
                  style="width: 100px; height: 100px; border-radius: 4px;"
                />
                <div class="d-flex flex-column">
                  <div class="d-flex flex-column mb-2">
                    <span class="d-block font-weight-bold mb-1">Title</span>
                    <span>{{ item.name }}</span>
                  </div>
                  <div class="d-flex flex-column">
                    <span class="d-block font-weight-bold mb-1">Description</span>
                    <span>{{ item.description }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </a-tab-pane>
    </a-tabs>
    <a-row class="footer-viewscreen footer d-flex justify-content-end">
      <a-button type="primary" @click="closeModal">OK</a-button>
    </a-row>
  </a-modal>
</template>

<script>
import apiClient from '@/services/axios'
import DefaultLabelTab from '@/views/apps/referral-program/components/DefaultLabelTab.vue'
import RulesetSkeleton from './RulesetSkeleton.vue'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'
import { Empty } from 'ant-design-vue'
import { subscribe, unsubscribe } from '@/composables/keydownEvent.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    languagesUsed: {
      type: Array,
      default: () => [],
    },
    selectedRuleset: {
      type: Object,
      default: null,
    },
  },
  components: {
    DefaultLabelTab,
    RulesetSkeleton,
    Empty,
  },
  data: () => ({
    showModal: false,
    translations: [],
    ruleset: {
      name: '',
      role: {
        name: '',
      },
      items: [],
    },
    loading: true,
  }),
  watch: {
    show: {
      handler(val) {
        if (val) {
          this.showModal = true
        } else {
          this.showModal = false
        }
      },
      deep: true,
      immediate: true,
    },
    showModal: {
      handler(val) {
        if (!val) {
          this.closeModal()
        }
      },
      deep: true,
    },
  },
  computed: {
    hasItems() {
      return !!this.ruleset?.items?.length
    },
  },
  methods: {
    async getRuleSet() {
      try {
        this.loading = true

        const url = `${referralApiUrl}/rule-sets/${this.selectedRuleset.id}`
        const response = await apiClient.get(url)
        const { data } = response?.data
        if (data) {
          this.ruleset = { ...this.ruleset, ...data }
          this.templateDataFilling()
          return
        }
        getErrorMessage(response?.data)
        return null
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    templateDataFilling() {
      if (!this.hasItems) {
        return
      }

      const groupedItems = {}

      this.ruleset.items.forEach(item => {
        const langId = item.language.id
        if (!groupedItems[langId]) {
          groupedItems[langId] = {
            language_id: langId,
            tab_name: item.language.name,
            is_default: item.language.is_default,
            items: [],
          }
        }
        groupedItems[langId].items.push({
          id: item.id,
          name: item.name,
          description: item.description,
          media: item.media,
        })
      })

      this.translations = Object.values(groupedItems).sort((a, b) => b.is_default - a.is_default)
    },
    clearForm() {
      this.translations = []
    },
    closeModal() {
      this.$emit('close')
      setTimeout(() => {
        this.clearForm()
      }, 500)
    },
  },
  mounted() {
    this.getRuleSet()
    subscribe(this.closeModal)
  },
  beforeDestroy() {
    unsubscribe()
  },
}
</script>

<style lang="scss">
.view-ruleset-modal {
  .tab-content-container {
    display: flex;
    flex-direction: column;
    gap: 20px;
    max-height: 500px;
    overflow-y: scroll;
  }

  .ant-tabs-card > .ant-tabs-content {
    margin-top: -16px;
  }

  .ant-tabs-card > .ant-tabs-content > .ant-tabs-tabpane {
    padding: 16px 8px 8px;
    border-right: 1px solid #e4e9f0;
    border-bottom: 1px solid #e4e9f0;
    border-left: 1px solid #e4e9f0;
    border-radius: 0 0 4px 4px;
  }

  .ant-tabs-card > .ant-tabs-bar .ant-tabs-tab {
    border-color: transparent;
    background: transparent;
    margin-right: 0 !important;
  }

  .footer {
    display: flex;
    justify-content: space-between;
    &::before,
    &::after {
      content: none;
    }
  }
}
</style>
